import { FormEvent, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AuthLayout } from '@/components/auth/AuthLayout';
import { TextField, FormError, FormSuccess, SubmitButton } from '@/components/auth/FormFields';
import { authApi, readApiError } from '@/lib/auth-api';

export function ForgotPasswordPage() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [pending, setPending] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setErr(null);
    setPending(true);
    try {
      await authApi.forgotPassword(email);
      setSent(true);
    } catch (caught) {
      setErr(readApiError(caught, t('auth.error_generic', { defaultValue: 'Something went wrong. Please try again.' }) as string));
    } finally {
      setPending(false);
    }
  }

  return (
    <AuthLayout
      title={t('auth.forgot_title', { defaultValue: 'Reset your password.' })}
      subtitle={
        sent
          ? t('auth.forgot_sent_subtitle', {
              defaultValue:
                "If an account exists for that email, we've sent a link to reset your password. Check your inbox — and your spam folder, just in case.",
            }) as string
          : t('auth.forgot_subtitle', {
              defaultValue: "We'll email you a link valid for one hour.",
            }) as string
      }
      footer={
        <span>
          <Link to="/login" className="text-accent-700 font-semibold hover:underline">
            ← {t('auth.back_to_login', { defaultValue: 'Back to sign in' })}
          </Link>
        </span>
      }
    >
      {!sent ? (
        <form onSubmit={onSubmit} className="space-y-4">
          <FormError message={err} />
          <TextField
            id="email"
            type="email"
            inputMode="email"
            label={t('auth.email_label', { defaultValue: 'Email' }) as string}
            placeholder="you@company.com"
            value={email}
            onChange={setEmail}
            autoComplete="email"
            autoFocus
            required
          />
          <SubmitButton pending={pending}>
            {t('auth.send_reset_link', { defaultValue: 'Send reset link' })}
          </SubmitButton>
        </form>
      ) : (
        <FormSuccess
          message={
            t('auth.forgot_success', {
              defaultValue: 'Check your inbox for a reset link.',
            }) as string
          }
        />
      )}
    </AuthLayout>
  );
}
